/**
 * MockBotworkClient — development-only fake client.
 * Returns realistic data without a network connection.
 * Enable via NEXT_PUBLIC_MOCK_P2P=true in .env.local
 */

import type {
  Agent,
  AgentDetails,
  TaskUpdate,
  RelayStatsResult,
  SearchAgentsParams,
  CreateTaskParams,
  CreateTaskResult,
  Signer,
  ConnectionState,
  BotworkClientOptions,
} from './types';

// ── Mock data ────────────────────────────────────────────────────

const MOCK_AGENTS: Agent[] = [
  {
    peerId: '12D3KooWMock1aaaaaaaaaaaaaaaaaaaaaaaaa',
    address: '0x1a2b3c4d5e6f7890abcdef1234567890abcdef01',
    name: 'MarketingGuru',
    categories: ['research', 'marketing', 'data'],
    priceRange: { min: 500_000000, max: 2500_000000 },
    description: 'Expert market research, competitive analysis, and strategy reports. Powered by Claude Opus 4.6.',
    rating: 4700,
    totalTasks: 1284,
    stakeAmount: 500_000000,
    online: true,
  },
  {
    peerId: '12D3KooWMock2bbbbbbbbbbbbbbbbbbbbbbbbb',
    address: '0x2b3c4d5e6f7890abcdef1234567890abcdef0102',
    name: 'ContentWriter',
    categories: ['writing', 'content', 'copywriting'],
    priceRange: { min: 200_000000, max: 1000_000000 },
    description: 'High-quality blog posts, landing pages, email campaigns, and social copy.',
    rating: 4500,
    totalTasks: 2041,
    stakeAmount: 250_000000,
    online: true,
  },
  {
    peerId: '12D3KooWMock3ccccccccccccccccccccccccc',
    address: '0x3c4d5e6f7890abcdef1234567890abcdef010203',
    name: 'DataAnalyzer',
    categories: ['data', 'analytics', 'visualization'],
    priceRange: { min: 300_000000, max: 1500_000000 },
    description: 'Data analysis, visualization, and insights from structured and unstructured datasets.',
    rating: 4800,
    totalTasks: 891,
    stakeAmount: 750_000000,
    online: true,
  },
  {
    peerId: '12D3KooWMock4ddddddddddddddddddddddddd',
    address: '0x4d5e6f7890abcdef1234567890abcdef01020304',
    name: 'CodeReviewer',
    categories: ['code', 'review', 'security'],
    priceRange: { min: 400_000000, max: 2000_000000 },
    description: 'Thorough code reviews, security audits, and refactoring suggestions for any language.',
    rating: 4900,
    totalTasks: 567,
    stakeAmount: 1000_000000,
    online: false,
  },
];

const MOCK_RELAY_STATS: RelayStatsResult = {
  leaderboard: [
    { peerId: '12D3KooWRelay1xxx', uptimeHours: 2160, trafficGB: 847.3, weight: 0.32 },
    { peerId: '12D3KooWRelay2yyy', uptimeHours: 1920, trafficGB: 623.1, weight: 0.24 },
    { peerId: '12D3KooWRelay3zzz', uptimeHours: 1680, trafficGB: 412.8, weight: 0.18 },
  ],
};

// ── Staged progress per category ─────────────────────────────────

const MOCK_PROGRESS_STAGES: Record<string, Array<{ stage: string; progress: number; delay: number }>> = {
  research: [
    { stage: 'Starting research phase...', progress: 5, delay: 1500 },
    { stage: 'Analyzing 12 sources across industry reports...', progress: 25, delay: 3500 },
    { stage: 'Found 3 key insights, compiling findings...', progress: 50, delay: 6000 },
    { stage: 'Generating strategic recommendations...', progress: 75, delay: 9000 },
    { stage: 'Formatting deliverables and citations...', progress: 90, delay: 11000 },
  ],
  writing: [
    { stage: 'Understanding brief and audience...', progress: 5, delay: 1500 },
    { stage: 'Researching topic and gathering references...', progress: 20, delay: 3000 },
    { stage: 'Writing first draft...', progress: 50, delay: 6000 },
    { stage: 'Editing and polishing tone...', progress: 75, delay: 9000 },
    { stage: 'Final formatting and proofread...', progress: 90, delay: 11000 },
  ],
  code: [
    { stage: 'Parsing codebase structure...', progress: 5, delay: 1500 },
    { stage: 'Analyzing 8 files for patterns and issues...', progress: 25, delay: 3500 },
    { stage: 'Identified 4 issues, writing recommendations...', progress: 55, delay: 6000 },
    { stage: 'Generating refactoring suggestions...', progress: 80, delay: 9000 },
    { stage: 'Compiling review report...', progress: 90, delay: 11000 },
  ],
  data: [
    { stage: 'Connecting to data sources...', progress: 5, delay: 1500 },
    { stage: 'Processing 2.4M rows...', progress: 30, delay: 4000 },
    { stage: 'Running statistical analysis...', progress: 55, delay: 7000 },
    { stage: 'Generating visualizations...', progress: 80, delay: 9500 },
    { stage: 'Formatting final report...', progress: 90, delay: 11000 },
  ],
  general: [
    { stage: 'Analyzing task requirements...', progress: 10, delay: 1500 },
    { stage: 'Gathering relevant information...', progress: 35, delay: 4000 },
    { stage: 'Synthesizing findings...', progress: 65, delay: 7000 },
    { stage: 'Preparing deliverables...', progress: 90, delay: 10000 },
  ],
};

const MOCK_TASK_RESULTS: Record<string, string> = {
  research: `## Tesla Competitive Analysis

### Summary
Comprehensive analysis of Tesla's competitive landscape across 3 key segments with 12 industry sources reviewed.

### Key Competitors
1. **BYD** — Leading in affordable EVs, dominates China market (35% share). Expanding aggressively into Europe with Seal and Dolphin models.
2. **Rivian** — Strong in adventure/truck segment, Amazon fleet deal securing $1.2B recurring revenue.
3. **Lucid** — Premium segment, best-in-class range (516 mi), targeting high-net-worth segment.

### Market Position
Tesla holds 18.2% global EV market share (down from 23% in 2024). Growth areas: Energy storage (+142% YoY), FSD licensing.

### Recommendations
1. Launch sub-$30k model to counter BYD's expansion into Europe
2. Accelerate FSD licensing deals with legacy automakers
3. Expand Megapack production to capture energy storage demand

### Attached Files
- competitive_analysis.pdf — Full 24-page report
- market_data.xlsx — Raw market share data by region`,

  writing: `# Your Brand Story

We crafted a compelling narrative that connects your mission to your audience's aspirations. The copy emphasizes authenticity, innovation, and community — three pillars identified from your brand audit.

### Deliverables
1. **Homepage hero copy** — 3 variants (bold, conversational, minimal)
2. **About page narrative** — 800-word brand story
3. **Social media bios** — Templates for 5 platforms
4. **Email welcome sequence** — 3-email onboarding flow

### Tone Guidelines
- Voice: Confident but approachable
- Avoid: Corporate jargon, passive voice
- Keywords: "build", "together", "future"

### Attached Files
- brand_copy.docx — All deliverables in one document
- style_guide.pdf — Tone and voice reference`,

  code: `## Code Review Report

### Summary
Reviewed 8 files, identified 4 issues (1 critical, 2 moderate, 1 minor).

### Critical Issues
1. **SQL injection vulnerability** in \`src/api/users.ts:47\` — user input passed directly to query builder without sanitization.

### Moderate Issues
2. **Memory leak** in \`src/hooks/useWebSocket.ts\` — event listener not cleaned up on unmount.
3. **Race condition** in \`src/services/cache.ts:89\` — concurrent cache writes can corrupt state.

### Minor Issues
4. **Unused imports** across 3 files — dead code that should be cleaned up.

### Recommendations
- Fix #1 immediately — use parameterized queries
- Fix #2 and #3 in next sprint
- Set up ESLint rule for unused imports

### Attached Files
- code_review.md — Detailed findings with line references`,

  default: `## Task Complete

### Summary
Analysis finished successfully with 3 key insights and 5 action items identified.

### Key Findings
1. Current approach has 15-20% improvement potential in target metrics
2. Two quick wins identified that can be implemented this week
3. Long-term strategy requires architectural changes in Q3

### Action Items
1. Implement quick win A (est. 2 hours)
2. Implement quick win B (est. 4 hours)
3. Schedule architecture review meeting
4. Create detailed spec for Q3 changes
5. Set up monitoring for new metrics

### Attached Files
- analysis_report.pdf — Full detailed report`,
};

// ── Mock client ─────────────────────────────────────────────────

export class MockBotworkClient {
  private state: ConnectionState = 'disconnected';
  private connectionCallbacks: Array<(state: ConnectionState) => void> = [];
  private taskUpdateCallbacks: Array<(update: TaskUpdate) => void> = [];
  private agentOnlineCallbacks: Array<(agent: { address: string; peerId: string }) => void> = [];
  private agentOfflineCallbacks: Array<(agent: { address: string }) => void> = [];
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(_options?: BotworkClientOptions) {}

  // ── Connection ─────────────────────────────────────────────────

  async connect(_gatewayUrl: string): Promise<void> {
    this.setState('connecting');
    await delay(300);
    this.setState('connected');
  }

  disconnect(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.setState('disconnected');
  }

  isConnected(): boolean {
    return this.state === 'connected' || this.state === 'authenticated';
  }

  isAuthenticated(): boolean {
    return this.state === 'authenticated';
  }

  getAddress(): string | null {
    return this.state === 'authenticated' ? '0xMockUser1234567890abcdef' : null;
  }

  onConnectionChange(cb: (state: ConnectionState) => void): void {
    this.connectionCallbacks.push(cb);
  }

  // ── Authentication ─────────────────────────────────────────────

  async authenticate(_signer: Signer): Promise<void> {
    await delay(500);
    this.setState('authenticated');
  }

  // ── Agent Discovery ────────────────────────────────────────────

  async searchAgents(params: SearchAgentsParams): Promise<Agent[]> {
    await delay(200);
    let agents = [...MOCK_AGENTS];

    if (params.category) {
      agents = agents.filter((a) => a.categories.includes(params.category!));
    }
    if (params.query) {
      const q = params.query.toLowerCase();
      agents = agents.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.categories.some((c) => c.includes(q)),
      );
    }
    if (params.minRating) {
      agents = agents.filter((a) => a.rating >= params.minRating!);
    }
    if (params.maxPrice) {
      agents = agents.filter((a) => a.priceRange.min <= params.maxPrice!);
    }

    return agents;
  }

  async getAgent(address: string): Promise<AgentDetails | null> {
    await delay(150);
    const agent = MOCK_AGENTS.find((a) => a.address === address);
    if (!agent) return null;
    return { ...agent, timestamp: Date.now() };
  }

  async getRelayStats(): Promise<RelayStatsResult> {
    await delay(200);
    return MOCK_RELAY_STATS;
  }

  onAgentOnline(cb: (agent: { address: string; peerId: string }) => void): void {
    this.agentOnlineCallbacks.push(cb);
  }

  onAgentOffline(cb: (agent: { address: string }) => void): void {
    this.agentOfflineCallbacks.push(cb);
  }

  // ── Tasks ──────────────────────────────────────────────────────

  async createTask(params: CreateTaskParams): Promise<CreateTaskResult> {
    const taskId = `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const category = params.category || 'general';
    const stages = MOCK_PROGRESS_STAGES[category] || MOCK_PROGRESS_STAGES.general;

    // Assigned notification
    const t0 = setTimeout(() => {
      this.emitTaskUpdate({
        taskId,
        status: 'assigned',
        agentAddress: params.agentAddress,
      });
    }, 1000);
    this.timers.push(t0);

    // Staged progress updates
    for (const { stage, progress, delay: stageDelay } of stages) {
      const t = setTimeout(() => {
        this.emitTaskUpdate({
          taskId,
          status: 'in_progress',
          progress,
          stage,
          agentAddress: params.agentAddress,
        });
      }, stageDelay);
      this.timers.push(t);
    }

    // Completion
    const lastDelay = stages[stages.length - 1]?.delay ?? 10000;
    const tDone = setTimeout(() => {
      const result = MOCK_TASK_RESULTS[category] || MOCK_TASK_RESULTS.default;
      this.emitTaskUpdate({
        taskId,
        status: 'completed',
        progress: 100,
        result,
        agentAddress: params.agentAddress,
        splits: { agent: 90, relay: 5, platform: 5 },
      });
    }, lastDelay + 2000);
    this.timers.push(tDone);

    return { taskId };
  }

  async approveTask(_taskId: string): Promise<void> {
    await delay(500);
  }

  async disputeTask(_taskId: string): Promise<void> {
    await delay(500);
  }

  subscribeTask(_taskId: string): void {}
  unsubscribeTask(_taskId: string): void {}

  onTaskUpdate(cb: (update: TaskUpdate) => void): void {
    this.taskUpdateCallbacks.push(cb);
  }

  // ── Cleanup ────────────────────────────────────────────────────

  destroy(): void {
    this.disconnect();
    this.connectionCallbacks = [];
    this.taskUpdateCallbacks = [];
    this.agentOnlineCallbacks = [];
    this.agentOfflineCallbacks = [];
  }

  // ── Internal ───────────────────────────────────────────────────

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    for (const cb of this.connectionCallbacks) {
      cb(state);
    }
  }

  private emitTaskUpdate(update: TaskUpdate): void {
    for (const cb of this.taskUpdateCallbacks) {
      cb(update);
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
